/* eslint-disable no-console */
import { useEffect, useState } from 'react';
import { RoomEvent, Track } from 'livekit-client';

/**
 * useRemoteGuestTracks — Phase 53 · Live Now guest hotfix.
 *
 * Tracks every REMOTE participant that is not the host and exposes their
 * published audio/video tracks, so both the broadcaster room (creator
 * must SEE and HEAR the guest) and the viewer room (audience sees the
 * guest tile next to the host feed) can render them.
 *
 * LiveKit identity === Nymia user_id (see livekit_provider.py), so the
 * host is filtered out by comparing `participant.identity` with the
 * live's `creator_id`.
 *
 * Returns: [{ identity, name, videoTrack, audioTrack }]
 */
export default function useRemoteGuestTracks(room, hostUserId) {
  const [guests, setGuests] = useState([]);

  useEffect(() => {
    if (!room) {
      setGuests([]);
      return undefined;
    }

    const rebuild = () => {
      const out = [];
      room.remoteParticipants.forEach((p) => {
        if (hostUserId && p.identity === hostUserId) return;
        let videoTrack = null;
        let audioTrack = null;
        p.trackPublications.forEach((pub) => {
          if (!pub.track) return;
          if (pub.track.kind === Track.Kind.Video) videoTrack = pub.track;
          else if (pub.track.kind === Track.Kind.Audio) audioTrack = pub.track;
        });
        if (videoTrack || audioTrack) {
          out.push({ identity: p.identity, name: p.name || p.identity, videoTrack, audioTrack });
        }
      });
      setGuests((prev) => {
        // Avoid useless re-renders when nothing changed.
        if (
          prev.length === out.length &&
          prev.every((g, i) => g.identity === out[i].identity
            && g.videoTrack === out[i].videoTrack
            && g.audioTrack === out[i].audioTrack)
        ) return prev;
        return out;
      });
    };

    const onSubscribed = (track, _pub, participant) => {
      console.info('[GUEST] remote track subscribed', {
        kind: track?.kind, identity: participant?.identity, name: participant?.name,
      });
      rebuild();
    };
    const onUnsubscribed = (_track, _pub, participant) => {
      console.info('[GUEST] remote track unsubscribed', { identity: participant?.identity });
      rebuild();
    };
    const onParticipantDisconnected = (participant) => {
      console.info('[GUEST] participant disconnected', { identity: participant?.identity });
      rebuild();
    };

    room.on(RoomEvent.TrackSubscribed, onSubscribed);
    room.on(RoomEvent.TrackUnsubscribed, onUnsubscribed);
    room.on(RoomEvent.ParticipantDisconnected, onParticipantDisconnected);
    room.on(RoomEvent.TrackMuted, rebuild);
    room.on(RoomEvent.TrackUnmuted, rebuild);
    rebuild();

    return () => {
      room.off(RoomEvent.TrackSubscribed, onSubscribed);
      room.off(RoomEvent.TrackUnsubscribed, onUnsubscribed);
      room.off(RoomEvent.ParticipantDisconnected, onParticipantDisconnected);
      room.off(RoomEvent.TrackMuted, rebuild);
      room.off(RoomEvent.TrackUnmuted, rebuild);
    };
  }, [room, hostUserId]);

  return guests;
}
